import { MarketStatsFilterParams } from '@monorepo/community-stats-common';
import {
  AggregationRequest,
  OptionalAssets,
  PaginateLeaderboardOptions,
  PaginationRequest,
} from '@monorepo/types';
import { QueryConfigKeys, QueryPriority } from './query-config';

export const QUERY_KEYS = {
  [QueryConfigKeys.AUTH]: {
    CURRENT_USER: [QueryConfigKeys.AUTH, 'current-user'],
  },
  [QueryConfigKeys.USERS]: {
    LIST: [QueryConfigKeys.USERS, 'list'],
    DETAILS: (id: string) => [QueryConfigKeys.USERS, 'details', id],
    CREDITS: (userId?: string) =>
      userId
        ? [QueryConfigKeys.USERS, 'credits', userId]
        : [QueryConfigKeys.USERS, 'credits'],
    CREDITS_HISTORY: [QueryConfigKeys.USERS, 'credits-history'],
    LOGINS: [QueryConfigKeys.USERS, 'logins'],
    CONTRACTS: [QueryConfigKeys.USERS, 'contracts'],
    COUPONS: [QueryConfigKeys.USERS, 'coupons'],
    COUPON_METRICS: [QueryConfigKeys.USERS, 'coupon-metrics'],
    RISE_ID: [QueryConfigKeys.USERS, 'rise-id'],
    DELETE_PROFILE_PICTURE: [QueryConfigKeys.USERS, 'delete-profile-picture'],
    UPLOAD_PROFILE_PICTURE: [QueryConfigKeys.USERS, 'upload-profile-picture'],
    PUBLIC_PROFILE: (id?: string) =>
      id
        ? [QueryConfigKeys.USERS, 'public-profile', id]
        : [QueryConfigKeys.USERS, 'public-profile', 'default'],
    VALID_USERNAME: (username: string) => [
      QueryConfigKeys.USERS,
      'valid-username',
      username,
    ],
  },
  [QueryConfigKeys.KYC]: {
    ACCESS_TOKEN: [QueryConfigKeys.KYC, 'access-token'],
    VERIFICATION_DATA: [QueryConfigKeys.KYC, 'verification-data'],
  },
  [QueryConfigKeys.PAYMENT_PROVIDERS]: {
    LIST: (currency: string) => [
      QueryConfigKeys.PAYMENT_PROVIDERS,
      'list',
      currency,
    ],
  },
  [QueryConfigKeys.AFFILIATES]: {
    DASHBOARD: [QueryConfigKeys.AFFILIATES, 'dashboard'],
    AFFILIATE_DISCOUNT: [QueryConfigKeys.AFFILIATES, 'affiliate-discount'],
    AVAILABLE_SUM: [QueryConfigKeys.AFFILIATES, 'available-sum'],
    PAYOUTS_TOTAL: (affiliateId: number) => [
      QueryConfigKeys.AFFILIATES,
      'payouts-total',
      affiliateId,
    ],
    WITHDRAWALS_COUNT: (affiliateId: number) => [
      QueryConfigKeys.AFFILIATES,
      'withdrawals-count',
      affiliateId,
    ],
    AFFILIATE_LIST: (params: PaginationRequest) => [
      QueryConfigKeys.AFFILIATES,
      'list',
      params.page,
      params.limit,
      ...(params.filters ? Object.entries(params.filters).flat() : []),
    ],
    FAQ: (application: string) => [
      QueryConfigKeys.AFFILIATES,
      'faq',
      application,
    ],
  },
  [QueryConfigKeys.ORDERS]: {
    LIST: ({ page, limit }: PaginationRequest) => [
      QueryConfigKeys.ORDERS,
      'list',
      page,
      limit,
    ],
    DETAILS: (id: string) => [QueryConfigKeys.ORDERS, 'details', id],
  },
  [QueryConfigKeys.PAYOUTS]: {
    LIST: ({
      page,
      limit,
      filters,
      type,
      sourceId,
    }: PaginationRequest & { type: string; sourceId: number }) => [
      QueryConfigKeys.PAYOUTS,
      'list',
      type,
      sourceId,
      page,
      limit,
      ...(filters ? Object.entries(filters).flat() : []),
    ],
    PAGINATE: (paginateRequest: PaginationRequest) => [
      QueryConfigKeys.PAYOUTS,
      'paginate',
      paginateRequest.page,
      paginateRequest.limit,
      ...Object.entries(paginateRequest.filters ?? {}).flat(),
    ],
    TOTALS_AND_COUNTS: (aggregationRequest: AggregationRequest) => [
      QueryConfigKeys.PAYOUTS,
      'totals-and-counts',
      ...Object.entries(aggregationRequest.filters ?? {}).flat(),
    ],
    TOTAL: (type: string, sourceId: string) => [
      QueryConfigKeys.PAYOUTS,
      'total',
      type,
      sourceId,
    ],
    ELIGIBILITY: (type: string, sourceId: string) => [
      QueryConfigKeys.PAYOUTS,
      'eligibility',
      type,
      sourceId,
    ],
    AIRWALLEX_AUTH: [QueryConfigKeys.PAYOUTS, 'airwallex-auth'],
    RISE_ID: [QueryConfigKeys.PAYOUTS, 'rise-id'],
  },
  [QueryConfigKeys.CURRENCIES]: {
    LIST: [QueryConfigKeys.CURRENCIES, 'list'],
    DETAILS: (id: string) => [QueryConfigKeys.CURRENCIES, 'details', id],
  },
  [QueryConfigKeys.APP_CONFIG]: {
    CONFIG: [QueryConfigKeys.APP_CONFIG, 'config'],
  },
  [QueryConfigKeys.CART]: {
    DETAILS: [QueryConfigKeys.CART, 'details'],
    DISCOUNTS: [QueryConfigKeys.CART, 'discounts'],
  },
  [QueryConfigKeys.PURCHASE]: {
    LIST: ({ page, limit, filters }: PaginationRequest) => [
      QueryConfigKeys.PURCHASE,
      'list',
      page,
      limit,
      ...(filters ? Object.entries(filters).flat() : []),
    ],
    DETAILS: (id: string) => [QueryConfigKeys.PURCHASE, 'details', id],
    INVOICE: (id: string) => [QueryConfigKeys.PURCHASE, 'invoice', id],
    STATUS: (id: string) => [QueryConfigKeys.PURCHASE, 'status', id],
    METRICS: [QueryConfigKeys.PURCHASE, 'metrics'],
  },
  [QueryConfigKeys.PRODUCTS]: {
    ASSET: (asset?: OptionalAssets) =>
      [QueryConfigKeys.PRODUCTS, 'asset', asset ? asset : 'empty'] as const,
    PROGRAMS_AND_GROUPS: () => [QueryConfigKeys.PRODUCTS, 'programs'],
  },
  [QueryConfigKeys.ADD_ONS]: {
    LIST: (accountId: string) => [QueryConfigKeys.ADD_ONS, 'list', accountId],
  }, // Added closing bracket for ADD_ONS
  [QueryConfigKeys.ACCOUNT]: {
    // Critical queries - required for dashboard initialization
    DASHBOARD: (sourceId: string) => [
      QueryPriority.CRITICAL,
      QueryConfigKeys.ACCOUNT,
      'dashboard',
      'account',
      sourceId,
    ],
    ACCOUNT_BALANCE: (tsAccountId: string) => [
      QueryPriority.CRITICAL,
      QueryConfigKeys.ACCOUNT,
      'account-balance',
      tsAccountId,
    ],
    // Secondary queries - important but non-blocking
    ADD_ONS: (accountId: string) => [
      QueryPriority.SECONDARY,
      QueryConfigKeys.ACCOUNT,
      'add-ons',
      accountId,
    ],
    KEY_METRICS: [
      QueryPriority.SECONDARY,
      QueryConfigKeys.ACCOUNT,
      'key-metrics',
    ],
    // Background queries - can load later
    PERFORMANCE_CHART: (accountId: string) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'performance-chart',
      accountId,
    ],
    ACTIVITY_LOG: [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'activity-log',
    ],
    ACCOUNT_STATISTICS: (tsAccountId: string) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'account-statistics',
      tsAccountId,
    ],
    CLOSED_POSITIONS: (accountId: string, page: number, limit: number) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'closed-positions',
      accountId,
      page,
      limit,
    ],
    OPEN_POSITIONS: (accountId: string, page: number, limit: number) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'open-positions',
      accountId,
      page,
      limit,
    ],
    ALL_POSITIONS: (accountId: string, page: number, limit: number) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'all-positions',
      accountId,
      page,
      limit,
    ],
    CONSISTENCY_RULE: (tsAccountId: string, rule: string) => [
      QueryPriority.SECONDARY,
      QueryConfigKeys.ACCOUNT,
      'consistency-rule',
      tsAccountId,
      rule,
    ],
    MIDNIGHT_VALUES: (tsAccountId: string) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'midnight-values',
      tsAccountId,
    ],
    POSITION_TRADES: (accountId: string, positionId: string) => [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'position-trades',
      accountId,
      positionId,
    ],
    SUPPORTED_SYMBOLS: [
      QueryPriority.BACKGROUND,
      QueryConfigKeys.ACCOUNT,
      'supported-symbols',
    ],
  },
  [QueryConfigKeys.PROGRAMS]: {
    ALL: [QueryPriority.CRITICAL, QueryConfigKeys.PROGRAMS, 'all'],
  },
  [QueryConfigKeys.CONTEST_CATEGORY]: {
    LIVE_CONTESTANTS: [QueryConfigKeys.CONTEST_CATEGORY, 'live-contestants'],
    CLOSED_WINNERS: [QueryConfigKeys.CONTEST_CATEGORY, 'closed-winners'],
    CLOSED_WINNERS_INFINITE: [
      QueryConfigKeys.CONTEST_CATEGORY,
      'closed-winners-infinite',
    ],
    USER_CONTEST_RANKING: [
      QueryConfigKeys.CONTEST_CATEGORY,
      'user-contest-ranking',
    ],
    CONTEST_INFORMATION: [
      QueryConfigKeys.CONTEST_CATEGORY,
      'contest-information',
    ],
  },
  [QueryConfigKeys.CONTEST_RANKINGS]: {
    LEADERBOARD_PREFIX: (contestId: string) =>
      [QueryConfigKeys.CONTEST_RANKINGS, 'leaderboard', contestId] as const,
    LEADERBOARD: (contestId: string, options?: PaginateLeaderboardOptions) =>
      [
        QueryConfigKeys.CONTEST_RANKINGS,
        'leaderboard',
        contestId,
        options,
      ] as const,
    LEADERBOARD_PODIUM: (
      contestId: string,
      options: PaginateLeaderboardOptions,
    ) =>
      [
        QueryConfigKeys.CONTEST_RANKINGS,
        'leaderboard',
        contestId,
        'podium',
        options,
      ] as const,
    LEADERBOARD_ALL_PAGES: (
      contestId: string,
      pageParams: Pick<
        PaginateLeaderboardOptions,
        'disqualified' | 'filters' | 'limit'
      >,
    ) =>
      [
        QueryConfigKeys.CONTEST_RANKINGS,
        'leaderboard',
        contestId,
        'all-pages',
        pageParams,
      ] as const,
  },
  [QueryConfigKeys.USER_CREDIT_HISTORY]: {
    LIST: ['user-credit-history', 'list'],
  },
  [QueryConfigKeys.CHART_LAYOUTS]: {
    SYMBOL: (symbol: string) => [
      QueryConfigKeys.CHART_LAYOUTS,
      'symbol',
      symbol,
    ],
    TEMPLATES_LIST: [QueryConfigKeys.CHART_LAYOUTS, 'templates', 'list'],
    TEMPLATE: (id: string) => [QueryConfigKeys.CHART_LAYOUTS, 'templates', id],
  },
  [QueryConfigKeys.COMMUNITY_STATS]: {
    PAYOUT_TICKER: [QueryConfigKeys.COMMUNITY_STATS, 'payout-ticker'],
    FILTERS: [QueryConfigKeys.COMMUNITY_STATS, 'filters'],
    DURATION_ANALYSIS: (symbol?: string, filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'duration-analysis',
      symbol ?? null,
      { ...filters },
    ],
    CORE_METRICS: (filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'core-metrics',
      { ...filters },
    ],
    DIRECTIONAL_ANALYTICS: (
      filters?: MarketStatsFilterParams,
      options?: { search?: string; limit?: number },
    ) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'directional-analytics',
      { ...filters },
      { ...options },
    ],
    AGREEMENT_GAUGE: (symbol?: string, filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'agreement-gauge',
      symbol ?? null,
      { ...filters },
    ],
    ASSET_DETAIL: (symbol: string, filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'asset-detail',
      symbol,
      { ...filters },
    ],
    PINNED_ASSETS: [QueryConfigKeys.COMMUNITY_STATS, 'pinned-assets'],
    RR_REALIZATION: (symbol?: string, filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'rr-realization',
      symbol ?? null,
      { ...filters },
    ],
    OPEN_ORDERS: (symbol: string, filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'open-orders',
      symbol,
      { ...filters },
    ],
    POSITIONS_MAP: (symbol: string, filters?: MarketStatsFilterParams) => [
      QueryConfigKeys.COMMUNITY_STATS,
      'positions-map',
      symbol,
      { ...filters },
    ],
  },
  [QueryConfigKeys.RELEASE]: {
    LAST_30_DAYS: [QueryConfigKeys.RELEASE, 'list', '30d'],
    DETAIL: (id: string) => [QueryConfigKeys.RELEASE, 'detail', id],
  },
} as const;
